import { apiClient } from './apiClient';

/**
 * Internal helper for RPC meet calls
 */
async function rpcMeet<T>(company: string, action: string, payload: any, options?: { timeout?: number }): Promise<T> {
  const { data } = await apiClient.post(`/api/${company}/rpc/meet/${action}`, payload, {
    withCredentials: true,
    timeout: options?.timeout, // Allow custom timeout for specific operations
  });
  
  if ((data as any)?.ok && 'data' in data) {
    return (data as any).data as T;
  }
  if ((data as any)?.ok) {
    const { ok, ...rest } = data as any;
    return rest as T;
  }
  return data as T;
}

/**
 * Get Google calendar connection status
 * Now uses RPC: POST /api/:company/rpc/meet/google/status
 */
export async function getGoogleStatus(company: string, adminId: string) {
  const result = await rpcMeet<{ connected: boolean }>(company, 'google/status', { adminId });
  return result;
}

/**
 * Get Google calendar events
 * Now uses RPC: POST /api/:company/rpc/meet/google/calendar-events
 */
export async function getGoogleCalendarEvents(company: string, adminId: string, date: string) {
  const result = await rpcMeet<{ events: any[] }>(company, 'google/calendar-events', { adminId, date });
  return result;
}

/**
 * Create Google Meet link
 * Now uses RPC: POST /api/:company/rpc/meet/google/meet
 */
export async function createGoogleMeet(company: string, payload: {
  adminId: string;
  clientEmail?: string;
  startTime: string;
  endTime: string;
}) {
  const result = await rpcMeet<{ meetLink: string }>(company, 'google/meet', payload);
  return result;
}

/**
 * Disconnect Google calendar
 * Now uses RPC: POST /api/:company/rpc/meet/google/disconnect
 */
export async function disconnectGoogle(company: string, adminId: string) {
  const result = await rpcMeet<{ ok: boolean }>(company, 'google/disconnect', { adminId });
  return result;
}

/**
 * Send email via Google
 * Now uses RPC: POST /api/:company/rpc/meet/google/send-email
 * Uses extended timeout (45 seconds) since email sending can take longer
 * (RabbitMQ queue timeout is 5s, then fallback to direct Gmail API which can take up to 40s)
 */
export async function sendGoogleEmail(company: string, payload: {
  adminId: string;
  to: string;
  subject: string;
  text?: string;
  html?: string;
  from?: string;
}) {
  // Use extended timeout (45 seconds) for email operations
  // RabbitMQ publish has 5s timeout, then fallback to direct Gmail API (can take up to 40s)
  const result = await rpcMeet<{ ok: boolean; id?: string; threadId?: string }>(
    company, 
    'google/send-email', 
    payload,
    { timeout: 45000 } // 45 seconds timeout for email operations (5s queue + 40s Gmail API)
  );
  return result;
}

/**
 * Send bulk email
 * Now uses RPC: POST /api/:company/rpc/meet/admin/bulk-email
 */
export async function sendBulkEmail(company: string, payload: {
  adminId: string;
  clientIds?: string[];
  emails?: string[];
  subject: string;
  text?: string;
  html?: string;
  from?: string;
}) {
  const result = await rpcMeet<{ ok: number; fail: number; total: number; results: any[] }>(
    company,
    'admin/bulk-email',
    payload
  );
  return result;
}

/**
 * Get Google OAuth auth URL for Calendar (default, minimal scopes)
 * Note: This is a redirect endpoint, so we keep it as GET for browser compatibility
 * Legacy: GET /api/:company/google/auth
 * 
 * @param company - Company slug
 * @param state - OAuth state (JSON stringified)
 * @param scopes - Optional: 'calendar' (default), 'gmail', or 'all'
 */
export function getGoogleAuthUrl(company: string, state: string, scopes: 'calendar' | 'gmail' | 'all' = 'calendar'): string {
  const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
  const url = new URL(`${baseUrl}/api/${company}/google/auth`);
  url.searchParams.set('state', state);
  if (scopes && scopes !== 'calendar') {
    url.searchParams.set('scopes', scopes);
  }
  return url.toString();
}

/**
 * Get Google OAuth auth URL for Gmail (incremental auth)
 * This requests Gmail scopes separately to avoid "unverified app" warning
 * 
 * @param company - Company slug
 * @param state - OAuth state (JSON stringified)
 */
export function getGoogleGmailAuthUrl(company: string, state: string): string {
  const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
  return `${baseUrl}/api/${company}/google/auth/gmail?state=${encodeURIComponent(state)}`;
}
